import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { mockCaseStudies } from "../mock";
import { ArrowRight, TrendingUp, Clock, MapPin } from "lucide-react";

const CaseStudiesPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-excel-steel to-excel-dark py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Nos {t("caseStudies")}
            </h1>
            <p className="text-xl max-w-3xl mx-auto leading-relaxed">
              Découvrez comment Excel Levage a transformé les opérations de nos clients avec des solutions innovantes et performantes.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {mockCaseStudies.map((study, index) => (
              <div key={study.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="space-y-4">
                    <Badge className="bg-excel-light-red text-white">
                      {t(study.sector)}
                    </Badge>
                    
                    <h2 className="text-3xl md:text-4xl font-bold text-excel-dark">
                      {t(study.title)}
                    </h2>
                    
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {t(study.description)}
                    </p>
                  </div>

                  {/* Project Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <MapPin className="h-5 w-5 text-excel-light-red" />
                      <div>
                        <p className="text-sm text-gray-500">Secteur</p>
                        <p className="font-medium text-excel-dark">{t(study.sector)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-excel-light-red" />
                      <div>
                        <p className="text-sm text-gray-500">Durée du projet</p>
                        <p className="font-medium text-excel-dark">3-6 mois</p>
                      </div>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-excel-dark flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-excel-light-red" />
                      Résultats obtenus :
                    </h3>
                    <div className="space-y-2">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <ArrowRight className="h-4 w-4 text-excel-light-red flex-shrink-0" />
                          <span className="text-gray-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button className="bg-excel-light-red hover:bg-excel-red text-white">
                      <Link to="/contact" className="flex items-center">
                        Discuter de votre projet
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                      <Link to="/products">
                        Voir nos solutions
                      </Link>
                    </Button>
                  </div>
                </div>

                <div className={`relative ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <img
                    src={study.image}
                    alt={t(study.title)}
                    className="w-full h-96 object-cover rounded-lg shadow-lg"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
                  
                  {/* Overlay with key metrics */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold text-excel-red">+20%</p>
                          <p className="text-sm text-gray-600">Productivité</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-excel-red">-30%</p>
                          <p className="text-sm text-gray-600">Arrêts</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-excel-dark mb-4">
              L'impact de nos solutions
            </h2>
            <p className="text-xl text-gray-600">
              Des résultats mesurables pour nos clients
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-excel-light-red w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-excel-dark mb-2">25%</div>
              <div className="text-gray-600">Amélioration moyenne de la productivité</div>
            </div>

            <div className="text-center">
              <div className="bg-excel-light-red w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-excel-dark mb-2">40%</div>
              <div className="text-gray-600">Réduction des temps d'arrêt</div>
            </div>

            <div className="text-center">
              <div className="bg-excel-light-red w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-excel-dark mb-2">99.8%</div>
              <div className="text-gray-600">Taux de satisfaction client</div>
            </div>

            <div className="text-center">
              <div className="bg-excel-light-red w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-excel-dark mb-2">15%</div>
              <div className="text-gray-600">Économies d'énergie moyennes</div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-excel-dark mb-4">
              Notre processus de réalisation
            </h2>
            <p className="text-xl text-gray-600">
              De l'analyse à la livraison, chaque étape est maîtrisée
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Analyse des besoins",
                description: "Étude approfondie de vos contraintes et objectifs"
              },
              {
                step: "02", 
                title: "Conception sur mesure",
                description: "Développement d'une solution adaptée à votre environnement"
              },
              {
                step: "03",
                title: "Fabrication & tests",
                description: "Production selon les normes les plus strictes"
              },
              {
                step: "04",
                title: "Installation & formation",
                description: "Mise en service et formation de vos équipes"
              }
            ].map((process, index) => (
              <Card key={index} className="relative border-l-4 border-l-excel-light-red">
                <CardHeader>
                  <div className="text-4xl font-bold text-excel-light-red mb-2">
                    {process.step}
                  </div>
                  <CardTitle className="text-excel-dark">{process.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{process.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-excel-steel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Prêt à rejoindre nos clients satisfaits ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour découvrir comment nous pouvons transformer vos opérations de levage.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg"
              className="bg-excel-light-red hover:bg-excel-red text-white"
            >
              <Link to="/contact">
                Discuter de votre projet
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudiesPage;